/**
 * BreadcrumbEditor
 *
 * Property editor for breadcrumb control type
 * Configures breadcrumb appearance, separators, and styling
 *
 * SRP: Responsible ONLY for configuring breadcrumb properties
 * Control Type: 'breadcrumb'
 *
 * Properties Configured (6):
 * 1. separator - Character between breadcrumbs
 * 2. textColor - Breadcrumb text color
 * 3. activeTextColor - Active item text color
 * 4. backgroundColor - Background color
 * 5. showHomeIcon - Display home icon at start
 * 6. maxItems - Maximum visible items before collapse
 */

import React, { useCallback, useMemo } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';
import { TextPropertyEditor } from '../BaseEditors/TextPropertyEditor';
import { ColorPropertyEditor } from '../BaseEditors/ColorPropertyEditor';
import { CheckboxPropertyEditor } from '../BaseEditors/CheckboxPropertyEditor';
import { NumberPropertyEditor } from '../BaseEditors/NumberPropertyEditor';

/**
 * Breadcrumb configuration
 */
interface BreadcrumbConfig {
  separator?: string;
  textColor?: string;
  activeTextColor?: string;
  backgroundColor?: string;
  showHomeIcon?: boolean;
  maxItems?: number;
}

/**
 * BreadcrumbEditor Component
 * Renders property editors for all breadcrumb configuration
 */
const BreadcrumbEditorComponent: React.FC<PropertyEditorProps> = ({
  control,
  onConfigChange,
}) => {
  const config = useMemo(
    () => (control.config as BreadcrumbConfig) || {},
    [control.config],
  );

  const handleSeparatorChange = useCallback(
    (value: string) => onConfigChange('separator', value),
    [onConfigChange],
  );

  const handleTextColorChange = useCallback(
    (value: string) => onConfigChange('textColor', value),
    [onConfigChange],
  );

  const handleActiveTextColorChange = useCallback(
    (value: string) => onConfigChange('activeTextColor', value),
    [onConfigChange],
  );

  const handleBackgroundColorChange = useCallback(
    (value: string) => onConfigChange('backgroundColor', value),
    [onConfigChange],
  );

  const handleShowHomeIconChange = useCallback(
    (value: boolean) => onConfigChange('showHomeIcon', value),
    [onConfigChange],
  );

  const handleMaxItemsChange = useCallback(
    (value: number) => onConfigChange('maxItems', value),
    [onConfigChange],
  );

  return (
    <div className="af-property-editor__section breadcrumb-editor">
      <h3 className="af-property-editor__section-title">Breadcrumb Properties</h3>

      <TextPropertyEditor
        label="Separator"
        value={config.separator || '/'}
        onChange={handleSeparatorChange}
        placeholder="e.g., / > or |"
        maxLength={3}
        helpText="Character(s) between breadcrumb items"
      />

      <ColorPropertyEditor
        label="Text Color"
        value={config.textColor || '#6B7879'}
        onChange={handleTextColorChange}
        helpText="Color for inactive breadcrumb items"
      />

      <ColorPropertyEditor
        label="Active Text Color"
        value={config.activeTextColor || '#309B48'}
        onChange={handleActiveTextColorChange}
        helpText="Color for the current/active breadcrumb"
      />

      <ColorPropertyEditor
        label="Background Color"
        value={config.backgroundColor || 'transparent'}
        onChange={handleBackgroundColorChange}
        helpText="Background color for breadcrumb container"
      />

      <CheckboxPropertyEditor
        label="Show Home Icon"
        value={config.showHomeIcon || false}
        onChange={handleShowHomeIconChange}
        helpText="Display home icon at the beginning"
      />

      <NumberPropertyEditor
        label="Max Items to Show"
        value={config.maxItems || 5}
        onChange={handleMaxItemsChange}
        min={1}
        max={20}
        step={1}
        helpText="Maximum visible items before collapsing"
      />
    </div>
  );
};

BreadcrumbEditorComponent.displayName = 'BreadcrumbEditor';

/**
 * Breadcrumb Property Editor Registration
 */
export const BreadcrumbEditor: PropertyEditor = {
  type: 'breadcrumb',
  label: 'Breadcrumb',
  description: 'Configure breadcrumb appearance, separators, and styling',
  version: '1.0.0',
  render: (props) => <BreadcrumbEditorComponent {...props} />,
};

export default BreadcrumbEditor;
